import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ErrorToast from "../../../../components/common/toasts/ErrorToast";
import ProgressBar from "../../../../components/common/ProgressBar";
import { premiumError, premiumRequest } from "../../../../services/Premium";
import { premiumPaths } from "../../navigation";
import useObjectUrl from "../../hooks/useObjectUrl";
import { Field, sourceLabel } from "../Fields";
import FileViewer from "../FileViewer";
import OrderFilePreview from "../OrderFilePreview";
import PremiumNotice from "../PremiumNotice";
import HighlightedPage from "./HighlightedPage";

function ItemSourcePage({ documentPath, source, hasRegion, document, token }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const path = hasRegion
    ? `${documentPath}/pages/${source.page}?rotation=${source.rotation ?? 0}`
    : documentPath;
  const ready = result?.path === path;
  const url = useObjectUrl(ready && hasRegion ? result.blob : null);
  useEffect(() => {
    const controller = new AbortController();
    setError("");
    setResult(null);
    premiumRequest(token, path, {
      responseType: "blob",
      signal: controller.signal,
    })
      .then((blob) => {
        if (!controller.signal.aborted) setResult({ path, blob });
      })
      .catch((reason) => {
        if (!controller.signal.aborted) setError(premiumError(reason));
      });
    return () => controller.abort();
  }, [token, path]);
  return (
    <>
      <ErrorToast message={error} />
      {(!ready || (hasRegion && !url)) && !error && (
        <ProgressBar label="Przygotowanie strony dokumentu" />
      )}
      {ready &&
        (!hasRegion || url) &&
        !error &&
        (hasRegion ? (
          <HighlightedPage
            key={path}
            url={url}
            regions={source.regions}
            onError={() =>
              setError("Nie udało się wyświetlić strony. Otwórz cały dokument.")
            }
            title={`${document.name} · strona ${source.page}`}
          />
        ) : (
          <FileViewer
            embedded
            file={{ blob: result.blob, name: document.name }}
            page={source.page}
          />
        ))}
    </>
  );
}

/** Pokazuje zapisane źródła wiersza lub odbiorcy bez ponownego uruchamiania OCR. */
export default function SourcePreview({ order, context, token, documentId }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const available = context.sources.filter((source) =>
    order.documents.some((doc) => doc.id === source.documentId),
  );
  const requested = Number(params.get("source") || 0);
  const index =
    Number.isInteger(requested) &&
    requested >= 0 &&
    requested < available.length
      ? requested
      : 0;
  const source = available[index];
  const document = order.documents.find(
    (doc) => doc.id === (source?.documentId || documentId),
  );
  if (!source) return <p>Nie znaleziono zapisanego źródła tych danych.</p>;
  const hasRegion = Boolean(source.page && source.regions?.length);
  const documentPath = `/orders/${order.id}/documents/${encodeURIComponent(source.documentId)}`;
  return (
    <section
      className={
        params.has("item")
          ? "card premium-panel premium-viewer"
          : "card premium-panel"
      }
    >
      <div className="premium-toolbar">
        <h2>{context.title}</h2>
        <Link
          className="btn btn-outline-primary"
          to={premiumPaths.document(order.id, document.id)}
        >
          Otwórz cały dokument
        </Link>
      </div>
      {hasRegion && (
        <p className="small text-muted">
          Zaznaczenie wskazuje dane odczytane z dokumentu. Wartości w formularzu
          mogą zawierać późniejsze poprawki.
        </p>
      )}
      {available.length > 1 ? (
        <Field
          label="Źródło danych"
          value={index}
          onChange={(event) => {
            const next = Number(event.target.value);
            const query = new URLSearchParams(params);
            query.set("source", next);
            // Link zachowuje identyfikator danych, a wybór strony nie tworzy historii powrotów.
            navigate(
              `${premiumPaths.document(order.id, available[next].documentId)}?${query}`,
              { replace: true },
            );
          }}
        >
          {available.map((entry, position) => (
            <option key={position} value={position}>
              {sourceLabel(entry, order.documents)} · źródło {position + 1}
            </option>
          ))}
        </Field>
      ) : (
        <p>{sourceLabel(source, order.documents)}</p>
      )}
      {!hasRegion && (
        <PremiumNotice>
          Nie możemy wskazać dokładnego fragmentu dla tych danych. Dokument jest
          dostępny bez zaznaczenia.
        </PremiumNotice>
      )}
      {params.has("item") ? (
        <ItemSourcePage
          documentPath={documentPath}
          source={source}
          hasRegion={hasRegion}
          document={document}
          token={token}
        />
      ) : (
        <OrderFilePreview
          key={index}
          path={documentPath}
          fileName={document.name}
          downloadLabel="Pobierz dokument"
          source={source}
        />
      )}
    </section>
  );
}
